'use client';

import key from 'weak-key';
import { Carousel, Container, Button } from '@geberit/gdds';

// styles
import styles from './media-overlay.module.scss';

// types
import type { MediaOverlayProps, MediaItem } from './types';

// components
import { Translation } from 'components/Translation/Translation';
import { CarouselItem } from './carousel-item';
import { VideoContent } from './video-content';

// utils
import { useIsMobile } from 'components/App/SizeProvider';
import { usePreviewIdBuilder } from 'utils/preview-id-builder';
import { useFocusTrap } from './focus-trap-hook';

function useImageSelection(isMobile: boolean) {
  return (mediaItem: MediaItem) => {
    return (
      (isMobile && mediaItem.pictureObjectMobile) || mediaItem.pictureObject || mediaItem.picture
    );
  };
}

export function MediaOverlay({ items, onClose, index }: Readonly<MediaOverlayProps>) {
  const isMobile = useIsMobile();
  const previewIdBuilder = usePreviewIdBuilder();
  const getImage = useImageSelection(isMobile);
  const isVideoOverlay = Boolean((items[0] as any)?.video);

  useFocusTrap(onClose);

  const carouselProps = {
    responsiveLayout: {
      small: { slides: 1, slidesToMove: 1 },
      medium: { slides: 1, slidesToMove: 1 },
      large: { slides: 1, slidesToMove: 1 },
    },
    startIndex: index,
    dark: true,
    hideButtons: true,
    supportMouse: true,
    customButtonLeft: isMobile || items.length === 1,
    customButtonRight: isMobile || items.length === 1,
    ...(items.length > 1 && { loop: true }),
  };

  const previewId = previewIdBuilder(isVideoOverlay ? '#st_videoChannel' : '#st_imageGallery');

  const renderContent = () => {
    if (isVideoOverlay && items[index]) {
      return <VideoContent mediaItem={items[index]} />;
    }

    return (
      <Carousel {...carouselProps}>
        {items.map((mediaItem, itemIndex) => (
          <CarouselItem
            key={key(mediaItem)}
            mediaItem={mediaItem}
            itemIndex={itemIndex}
            getImage={getImage}
          />
        ))}
      </Carousel>
    );
  };

  return (
    <div className={styles.overlayWrapper} role="dialog" aria-modal="true">
      <Container className={styles.overlayContainer}>
        <Button
          stylingType="flatInverted"
          isIcon={isMobile}
          iconAlignment="right"
          symbol="close"
          tabIndex={0}
          onClick={onClose}
          className={styles.overlayCloseButton}
          aria-label={isMobile ? 'Close' : undefined}
        >
          {!isMobile && <Translation id="web20_lightbox_close" />}
        </Button>

        <div data-preview-id={previewId}>{renderContent()}</div>
      </Container>
    </div>
  );
}
